"use client"

import {
  type ChangeEvent,
  type RefObject,
  type SubmitEvent,
  useRef,
  useState,
} from "react"

type UseNewsletterFormApi = {
  readonly isEmailValid: boolean
  readonly isFormValid: boolean
  readonly onEmailChange: (event: ChangeEvent<HTMLInputElement>) => void
  readonly onTermsChange: (event: ChangeEvent<HTMLInputElement>) => void
  readonly handleSubmit: (event: SubmitEvent<HTMLFormElement>) => void
  readonly honeypotRef: RefObject<HTMLInputElement | null>
}

export function useNewsletterForm(
  onSubmit: (
    email: string,
    isTermsAccepted: boolean,
    honeypotValue: string | undefined,
  ) => void,
): UseNewsletterFormApi {
  const [isEmailValid, setIsEmailValid] = useState(false)
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)
  const honeypotRef = useRef<HTMLInputElement>(null)

  const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsEmailValid(event.target.validity.valid)
  }

  const onTermsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsTermsAccepted(event.target.checked)
  }

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!isEmailValid || !isTermsAccepted) {
      return
    }

    const formData = new FormData(event.currentTarget)
    const email = formData.get("email")?.toString() ?? ""

    const honeypotValue = honeypotRef.current?.value.trim()

    onSubmit(email, isTermsAccepted, honeypotValue)
  }

  return {
    handleSubmit,
    honeypotRef,
    isEmailValid,
    isFormValid: isEmailValid && isTermsAccepted,
    onEmailChange,
    onTermsChange,
  }
}
